import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Badge } from 'components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import api, { getApiErrorMessage } from 'lib/api';
import { ArrowLeft, BadgeCheck, MapPin, Star, Heart } from 'lucide-react';
import { formatDateTime } from 'lib/format';
import { toast } from 'sonner';
import { useAuth } from 'contexts/AuthContext';
import { useLanguage } from 'lib/i18n';

const roleTranslationKeys = {
  sender: 'role.sender',
  traveler: 'role.traveler',
  deliverer: 'role.deliverer',
};

export default function PublicProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reputation, setReputation] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('fraud');
  const [reportDescription, setReportDescription] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/users/${userId}`),
      api.get(`/reviews/user/${userId}`),
      api.get(`/reviews/user/${userId}/stats`),
    ])
      .then(([profileResponse, reviewsResponse, reputationResponse]) => {
        setProfile(profileResponse.data);
        setReviews(Array.isArray(reviewsResponse.data) ? reviewsResponse.data : []);
        setReputation(reputationResponse.data);
      })
      .catch((err) => toast.error(getApiErrorMessage(err, t('publicProfile.unavailable'))));
  }, [userId]);

  if (!profile) {
    return <DashboardShell><div className="text-muted-foreground">{t('publicProfile.loading')}</div></DashboardShell>;
  }

  const rating = Number(profile.rating || 0);
  const initials = (profile.full_name || 'U').split(' ').map((part) => part[0]).slice(0, 2).join('');
  const isOwnProfile = user?.id === profile.id;
  const isBlocked = (user?.blocked_user_ids || []).includes(profile.id);
  const isFavorite = (user?.favorite_deliverer_ids || []).includes(profile.id);
  const isDeliverer = (profile.roles || []).includes('deliverer') || (profile.roles || []).includes('traveler');

  const toggleBlock = async () => {
    if (!isBlocked && !window.confirm(t('publicProfile.blockConfirm').replace('{name}', profile.full_name || t('publicProfile.thisUser')))) return;
    try {
      await api.post(`/users/${profile.id}/${isBlocked ? 'unblock' : 'block'}`);
      toast.success(isBlocked ? t('publicProfile.unblocked') : t('publicProfile.blocked'));
      refreshUser();
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('publicProfile.actionImpossible')));
    }
  };

  const toggleFavorite = async () => {
    try {
      await api.post(`/users/${profile.id}/${isFavorite ? 'unfavorite' : 'favorite'}`);
      toast.success(isFavorite ? t('publicProfile.removedFavorite') : t('publicProfile.addedFavorite'));
      refreshUser();
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('publicProfile.actionImpossible')));
    }
  };

  const submitReport = async () => {
    setSubmittingReport(true);
    try {
      await api.post(`/users/${profile.id}/report`, { reason: reportReason, description: reportDescription || undefined });
      toast.success(t('publicProfile.reportSent'));
      setReportOpen(false);
      setReportDescription('');
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('publicProfile.reportError')));
    } finally {
      setSubmittingReport(false);
    }
  };

  return (
    <DashboardShell>
      <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate(-1)}>
        <ArrowLeft size={16} className="mr-1" /> {t('publicProfile.back')}
      </Button>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6 text-center">
            <Avatar className="h-24 w-24 mx-auto mb-4">
              <AvatarImage src={profile.avatar_url || ''} />
              <AvatarFallback className="bg-primary text-primary-foreground text-2xl">{initials}</AvatarFallback>
            </Avatar>
            <h1 className="text-xl font-semibold">{profile.full_name || t('publicProfile.user')}</h1>
            <div className="flex flex-wrap justify-center gap-1 mt-3">
              {(profile.roles || []).map((role) => <Badge key={role} variant="outline">{roleTranslationKeys[role] ? t(roleTranslationKeys[role]) : role}</Badge>)}
            </div>
            {rating > 0 ? (
              <div className="mt-4 flex items-center justify-center gap-1 text-sm">
                <Star size={15} className="fill-amber-500 text-amber-500" />
                <span className="font-medium">{rating.toFixed(1)}</span>
                <span className="text-muted-foreground">({t('publicProfile.reviewCount').replace('{count}', profile.review_count || reviews.length)})</span>
              </div>
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">{t('publicProfile.noReviewsYet')}</p>
            )}
            <div className="mt-4 space-y-2 text-sm">
              {profile.city && profile.country && (
                <div className="flex items-center justify-center gap-1 text-muted-foreground"><MapPin size={14} /> {profile.city}, {profile.country}</div>
              )}
              {profile.is_verified_email && <div className="flex items-center justify-center gap-1 text-emerald-700"><BadgeCheck size={14} /> {t('publicProfile.emailVerified')}</div>}
              {profile.is_verified_id && <div className="flex items-center justify-center gap-1 text-emerald-700"><BadgeCheck size={14} /> {t('publicProfile.identityVerified')}</div>}
              {profile.deliverer_validated && <div className="flex items-center justify-center gap-1 text-emerald-700"><BadgeCheck size={14} /> {t('publicProfile.courierVerified')}</div>}
            </div>
            {profile.bio && <p className="mt-5 text-sm text-left text-muted-foreground whitespace-pre-wrap">{profile.bio}</p>}
            {!isOwnProfile && (
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {isDeliverer && (
                  <Button size="sm" variant={isFavorite ? 'secondary' : 'outline'} onClick={toggleFavorite}>
                    <Heart size={14} className={`mr-1 ${isFavorite ? 'fill-current' : ''}`} /> {isFavorite ? t('publicProfile.favorite') : t('publicProfile.addToFavorites')}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={toggleBlock}>{isBlocked ? t('publicProfile.unblock') : t('publicProfile.block')}</Button>
                <Button size="sm" variant="ghost" onClick={() => setReportOpen(true)}>{t('publicProfile.report')}</Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">{t('publicProfile.reviewsReceived')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {reputation && <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 border-b pb-4 text-center text-sm">
              <div><div className="font-semibold">{reputation.missions_delivered}</div><div className="text-xs text-muted-foreground">{t('publicProfile.successfulDeliveries')}</div></div>
              <div><div className="font-semibold">{Math.round(reputation.delivery_success_rate * 100)}%</div><div className="text-xs text-muted-foreground">{t('publicProfile.successRate')}</div></div>
              <div><div className="font-semibold">{reputation.punctuality || '-'}/5</div><div className="text-xs text-muted-foreground">{t('publicProfile.punctuality')}</div></div>
              <div><div className="font-semibold">{reputation.communication || '-'}/5</div><div className="text-xs text-muted-foreground">{t('publicProfile.communication')}</div></div>
              <div><div className="font-semibold">{Math.round((1 - reputation.cancellation_rate) * 100)}%</div><div className="text-xs text-muted-foreground">{t('publicProfile.missionsKept')}</div></div>
            </div>}
            {reviews.length === 0 && <p className="text-sm text-muted-foreground">{t('publicProfile.noReviewsReceived')}</p>}
            {reviews.map((review) => (
              <div key={review.id} className="border-b last:border-0 pb-4 last:pb-0">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium text-sm">{review.reviewer_name || t('publicProfile.user')}</div>
                  <div className="flex items-center gap-1 text-sm">
                    <Star size={14} className="fill-amber-500 text-amber-500" /> {review.rating}/5
                  </div>
                </div>
                {review.comment && <p className="mt-2 text-sm text-muted-foreground">{review.comment}</p>}
                {review.created_at && <p className="mt-2 text-xs text-muted-foreground">{formatDateTime(review.created_at)}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('publicProfile.reportTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={reportReason} onChange={(event) => setReportReason(event.target.value)}>
              <option value="fraud">{t('publicProfile.reason.fraud')}</option>
              <option value="harassment">{t('publicProfile.reason.harassment')}</option>
              <option value="spam">{t('publicProfile.reason.spam')}</option>
              <option value="prohibited_content">{t('publicProfile.reason.prohibited')}</option>
              <option value="impersonation">{t('publicProfile.reason.impersonation')}</option>
              <option value="other">{t('publicProfile.reason.other')}</option>
            </select>
            <textarea className="min-h-24 w-full rounded-md border border-input bg-background p-3 text-sm" value={reportDescription} onChange={(event) => setReportDescription(event.target.value)} placeholder={t('publicProfile.reportDescriptionPlaceholder')} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)}>{t('publicProfile.cancel')}</Button>
            <Button onClick={submitReport} disabled={submittingReport}>{submittingReport ? t('publicProfile.sending') : t('publicProfile.sendReport')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
